'use client';

import clsx from 'clsx';
import type { FocusEvent, KeyboardEvent, MouseEvent, PointerEvent, ReactElement } from 'react';
import { useId, useRef, useState } from 'react';

import { SDSChevronIcon } from '@repo/sds/SDSChevronIcon';
import { SDSPrimitiveButton, type SDSPrimitiveButtonProps } from '@repo/sds/SDSPrimitiveButton';
import type { SDSTextType } from '@repo/sds/SDSText';
import { SDSText } from '@repo/sds/SDSText';
import type { SubMenuItem } from '@repo/ui/fixtures/header/types/menu-items';

import { DropdownItem } from '../../../../components/elements/DropdownItem/DropdownItem';

import './HeaderLinkButton.scss';

interface HeaderLinkButtonProps {
  icon?: ReactElement;
  href?: string;
  text: string;
  subMenuItems?: SubMenuItem[];
  textType?: SDSTextType;
  variant?: 'desktop' | 'mobile';
  className?: string;
  dataTestId?: string;
}

function useSubmenuInteractions(hasSubmenu: boolean, variant: 'desktop' | 'mobile') {
  const [isSubmenuOpen, setIsSubmenuOpen] = useState(false);
  const containerRef = useRef<HTMLLIElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const openedByHoverRef = useRef(false);
  const openedExplicitlyRef = useRef(false);
  const submenuId = useId();

  function focusFirstSubmenuItem() {
    window.requestAnimationFrame(() => {
      containerRef.current?.querySelector<HTMLAnchorElement>('.HeaderLinkButton__submenu a')?.focus();
    });
  }

  function handleTriggerClick(event: MouseEvent<HTMLButtonElement> | KeyboardEvent<HTMLButtonElement>) {
    event.preventDefault();

    if (openedByHoverRef.current) {
      openedByHoverRef.current = false;
      openedExplicitlyRef.current = true;
      setIsSubmenuOpen(true);
      return;
    }

    const nextIsSubmenuOpen = !isSubmenuOpen;
    openedExplicitlyRef.current = nextIsSubmenuOpen;
    setIsSubmenuOpen(nextIsSubmenuOpen);
  }

  function handleContainerKeyDown(event: KeyboardEvent<HTMLLIElement>) {
    if (!hasSubmenu) {
      return;
    }

    if (event.key === 'ArrowDown' && event.target === triggerRef.current) {
      event.preventDefault();
      openedByHoverRef.current = false;
      openedExplicitlyRef.current = true;
      setIsSubmenuOpen(true);
      focusFirstSubmenuItem();
    }

    if (event.key === 'Escape') {
      event.preventDefault();
      openedByHoverRef.current = false;
      openedExplicitlyRef.current = false;
      setIsSubmenuOpen(false);
      triggerRef.current?.focus();
    }
  }

  function handleBlur(event: FocusEvent<HTMLLIElement>) {
    if (!event.currentTarget.contains(event.relatedTarget)) {
      openedByHoverRef.current = false;
      openedExplicitlyRef.current = false;
      setIsSubmenuOpen(false);
    }
  }

  function handlePointerEnter(event: PointerEvent<HTMLLIElement>) {
    if (variant === 'desktop' && hasSubmenu && event.pointerType === 'mouse') {
      if (!openedExplicitlyRef.current) {
        openedByHoverRef.current = true;
      }
      setIsSubmenuOpen(true);
    }
  }

  function handlePointerLeave(event: PointerEvent<HTMLLIElement>) {
    if (event.pointerType !== 'mouse') {
      return;
    }

    openedByHoverRef.current = false;
    if (!(openedExplicitlyRef.current || containerRef.current?.contains(document.activeElement))) {
      setIsSubmenuOpen(false);
    }
  }

  return {
    containerRef,
    triggerRef,
    submenuId,
    isSubmenuOpen,
    handleBlur,
    handleContainerKeyDown,
    handlePointerEnter,
    handlePointerLeave,
    handleTriggerClick,
  };
}

export function HeaderLinkButton({
  icon,
  href,
  text,
  subMenuItems,
  textType = 'body-medium',
  variant = 'desktop',
  className = '',
  dataTestId,
}: HeaderLinkButtonProps) {
  const menuItems = subMenuItems ?? [];
  const hasSubmenu = menuItems.length > 0;
  const {
    containerRef,
    triggerRef,
    submenuId,
    isSubmenuOpen,
    handleBlur,
    handleContainerKeyDown,
    handlePointerEnter,
    handlePointerLeave,
    handleTriggerClick,
  } = useSubmenuInteractions(hasSubmenu, variant);
  const triggerProps: SDSPrimitiveButtonProps = hasSubmenu
    ? {
        buttonRef: triggerRef,
        onClick: handleTriggerClick,
        ariaExpanded: isSubmenuOpen,
        ariaControls: submenuId,
      }
    : { href };

  return (
    <li
      ref={containerRef}
      className={clsx('HeaderLinkButton__container', className, {
        'HeaderLinkButton__container--open': isSubmenuOpen,
      })}
      onPointerEnter={handlePointerEnter}
      onPointerLeave={handlePointerLeave}
      onBlur={handleBlur}
      onKeyDown={handleContainerKeyDown}
    >
      <SDSPrimitiveButton {...triggerProps} className="HeaderLinkButton" dataTestId={dataTestId}>
        {icon}
        <SDSText text={text} type={textType} className="HeaderLinkButton__icon" />
        {hasSubmenu && <SDSChevronIcon className="HeaderLinkButton__chevron" isExpanded={isSubmenuOpen} />}
      </SDSPrimitiveButton>
      {hasSubmenu && (
        <ul id={submenuId} className={clsx('HeaderLinkButton__submenu', `HeaderLinkButton__submenu--${variant}`)}>
          {menuItems.map(item => (
            <li key={item.text}>
              <DropdownItem href={item.href} className="HeaderLinkButton__dropdownItems">
                <SDSText text={item.text} type={textType} />
              </DropdownItem>
            </li>
          ))}
        </ul>
      )}
    </li>
  );
}
